import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { CaretLeft, Plus, Trash, Camera, Image, Eye, Heart, CircleNotch } from '@phosphor-icons/react';

export default function PortfolioPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: user?.trade || '' });
  const [beforePhoto, setBeforePhoto] = useState(null);
  const [afterPhoto, setAfterPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get('/portfolio/my'); setItems(data.items || []); } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [api]);

  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append('file', file);
    const { data } = await api.post('/upload/photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data.photo_id;
  };

  const handleSubmit = async () => {
    setUploading(true);
    try {
      let beforeId = '', afterId = '';
      if (beforePhoto) beforeId = await uploadPhoto(beforePhoto);
      if (afterPhoto) afterId = await uploadPhoto(afterPhoto);
      await api.post('/portfolio', { ...form, before_photo_id: beforeId, after_photo_id: afterId });
      setShowAdd(false); setForm({ title: '', description: '', category: user?.trade || '' });
      setBeforePhoto(null); setAfterPhoto(null); load();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setUploading(false); }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this portfolio item?')) return;
    try { await api.delete(`/portfolio/${id}`); load(); } catch {}
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] pb-24 max-w-[480px] mx-auto" data-testid="portfolio-page">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#C9BAA8]/40 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="w-9 h-9 bg-[#DFD3C5] rounded-full flex items-center justify-center"><CaretLeft size={18} /></button>
        <h1 className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">My Portfolio</h1>
        <button onClick={() => setShowAdd(!showAdd)} className="bg-[#E86A33] text-white font-bold text-xs px-3 py-2 rounded-lg flex items-center gap-1" data-testid="add-portfolio-button">
          <Plus size={14} /> Add Work
        </button>
      </header>

      <div className="p-4 space-y-4">
        {/* Add Form */}
        {showAdd && (
          <div className="bg-white border-2 border-[#E86A33] rounded-2xl p-5 shadow-md" data-testid="portfolio-add-form">
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-4">Showcase Your Work</h3>
            <div className="space-y-3">
              <input data-testid="portfolio-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Job title (e.g. 'Kitchen Pipe Replacement')" className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-xl px-4 py-3 font-body outline-none focus:border-[#E86A33]" />
              <textarea data-testid="portfolio-desc" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the work you did..." rows={3} className="w-full bg-[#E8DDD2] border border-[#D4C5B3] rounded-xl px-4 py-3 font-body outline-none focus:border-[#E86A33] resize-none" />

              {/* Photo Uploads */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#8C8178] mb-2">Before Photo</p>
                  <label className={`block w-full aspect-square rounded-xl border-2 border-dashed cursor-pointer overflow-hidden transition-all ${beforePhoto ? 'border-[#E86A33]' : 'border-[#C9BAA8] hover:border-[#E86A33]/50'}`} data-testid="upload-before">
                    {beforePhoto ? (
                      <img src={URL.createObjectURL(beforePhoto)} alt="Before" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-[#8C8178]">
                        <Camera size={28} className="mb-1" /><span className="text-[10px] font-bold">Before</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={e => setBeforePhoto(e.target.files?.[0] || null)} />
                  </label>
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#8C8178] mb-2">After Photo</p>
                  <label className={`block w-full aspect-square rounded-xl border-2 border-dashed cursor-pointer overflow-hidden transition-all ${afterPhoto ? 'border-[#2D6A4F]' : 'border-[#C9BAA8] hover:border-[#2D6A4F]/50'}`} data-testid="upload-after">
                    {afterPhoto ? (
                      <img src={URL.createObjectURL(afterPhoto)} alt="After" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-[#8C8178]">
                        <Camera size={28} className="mb-1" /><span className="text-[10px] font-bold">After</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={e => setAfterPhoto(e.target.files?.[0] || null)} />
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowAdd(false)} className="flex-1 bg-[#DFD3C5] text-[#5C5C5C] font-bold py-3 rounded-xl font-body">Cancel</button>
                <button onClick={handleSubmit} disabled={!form.title || uploading} className="flex-1 bg-[#1A4D2E] text-white font-bold py-3 rounded-xl font-body disabled:opacity-50 flex items-center justify-center gap-2" data-testid="save-portfolio-button">
                  {uploading ? <><CircleNotch size={16} className="animate-spin" /> Uploading...</> : 'Save Work'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Portfolio Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-3 border-[#D4C5B3] border-t-[#E86A33] rounded-full animate-spin" /></div>
        ) : items.length === 0 && !showAdd ? (
          <div className="text-center py-16">
            <Image size={48} className="text-[#D4C9BC] mx-auto mb-4" />
            <h3 className="font-heading text-lg font-bold text-[#1C1C1C] mb-2">No work to show yet</h3>
            <p className="text-sm text-[#8C8178] mb-4 font-body">Add before/after photos of your completed jobs to build trust with customers.</p>
            <button onClick={() => setShowAdd(true)} className="bg-[#E86A33] text-white font-bold px-6 py-3 rounded-xl font-body">Add Your First Work</button>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map(item => (
              <div key={item.id} className="bg-white rounded-2xl border border-[#D4C5B3] overflow-hidden shadow-sm hover:-translate-y-0.5 transition-all" data-testid={`portfolio-item-${item.id}`}>
                {/* Before/After */}
                {(item.before_photo_data || item.after_photo_data) && (
                  <div className="grid grid-cols-2 gap-px bg-[#E5E0D8]">
                    <div className="relative bg-[#E8DDD2] aspect-[4/3]">
                      {item.before_photo_data ? <img src={item.before_photo_data} alt="Before" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#8C8178] text-xs">No before photo</div>}
                      <span className="absolute top-2 left-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm">BEFORE</span>
                    </div>
                    <div className="relative bg-[#E8DDD2] aspect-[4/3]">
                      {item.after_photo_data ? <img src={item.after_photo_data} alt="After" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#8C8178] text-xs">No after photo</div>}
                      <span className="absolute top-2 left-2 bg-[#2D6A4F]/90 text-white text-[10px] font-bold px-2 py-0.5 rounded-full backdrop-blur-sm">AFTER</span>
                    </div>
                  </div>
                )}
                <div className="p-4">
                  <h4 className="font-heading text-base font-bold text-[#1C1C1C]">{item.title}</h4>
                  {item.description && <p className="text-xs text-[#8C8178] mt-1 font-body leading-relaxed">{item.description}</p>}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#DDD0C1]">
                    <div className="flex items-center gap-3 text-xs text-[#8C8178]">
                      <span className="flex items-center gap-1"><Eye size={12} /> {item.views || 0}</span>
                      <span className="font-bold px-2 py-0.5 rounded-full bg-[#1A4D2E]/5 text-[#1A4D2E]">{item.category?.replace(/_/g, ' ')}</span>
                    </div>
                    <button onClick={() => deleteItem(item.id)} className="text-red-400 hover:text-red-600" data-testid={`delete-portfolio-${item.id}`}><Trash size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Browse others link */}
        <Link to="/portfolios" className="block text-center text-sm text-[#E86A33] font-bold mt-4" data-testid="browse-portfolios-link">Browse All Artisan Portfolios →</Link>
      </div>
    </div>
  );
}
